import React, { useState, useEffect, useCallback, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { ExperienceCard } from './experience-card';
import { PlaceHolderImages } from '../lib/placeholder-images';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const experiences = [
  {
    id: "ksa-camel-farm",
    title: "Camel Farm & Desert Retreat",
    description: "Experience traditional desert life with a visit to a camel farm."
  },
  {
    id: "ksa-boulevard-world",
    title: "Boulevard World (Riyadh Season)",
    description: "Explore global cultures, cuisines, and entertainment in one place."
  },
  {
    id: "ksa-diriyah-art",
    title: "Diriyah Art Walk",
    description: "Discover contemporary art installations in a historic setting."
  },
  {
    id: "ksa-mecca",
    title: "Pilgrimage to Mecca",
    description: "Embark on a spiritual journey to the heart of Islam."
  },
  {
    id: "ksa-medina",
    title: "Medina's Sacred Sites",
    description: "Visit the holy and historic sites in the city of Medina."
  },
  {
    id: "ksa-edge-of-world",
    title: "Edge of the World Hike",
    description: "Witness breathtaking views from the dramatic Tuwaiq cliffs."
  },
  {
    id: "ksa-alula-tour",
    title: "AlUla Heritage Tour",
    description: "Explore ancient tombs and stunning rock formations in a UNESCO site."
  },
  {
    id: "ksa-salt-flat",
    title: "Salt Flat Safari at Al Kharrarah",
    description: "Experience the unique landscape of the Al Kharrarah salt flats."
  },
  {
    id: "ksa-riyadh-shopping",
    title: "Shopping in Riyadh Boulevard",
    description: "Indulge in luxury shopping and fine dining in the capital."
  },
  {
    id: "ksa-jeddah-diving",
    title: "Jeddah’s Red Sea Diving",
    description: "Discover the vibrant coral reefs and marine life of the Red Sea."
  }
];

export default function KsaTopExperiences() {
  const autoplay = useRef(Autoplay({ delay: 3000, stopOnInteraction: false }));
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, slidesToScroll: 1, align: 'center' },
    [autoplay.current]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback((api:any) => {
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const selectedExperience = experiences[selectedIndex];

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <div className="container mx-auto px-4">
      <div className="text-center mb-12">
        <h2 className="text-4xl md:text-5xl font-bold text-primary">
          Top Experiences in KSA
        </h2>
        <p className="mt-4 text-lg text-muted-foreground">
          Discover unforgettable attractions and activities in the Kingdom.
        </p>
      </div>

      <div className="relative">
        <div className="overflow-hidden w-full" ref={emblaRef}>
          <div className="flex -ml-4">
            {experiences.map((experience, index) => {
              const imageData = PlaceHolderImages.find(p => p.id === experience.id);
              if (!imageData) return null;

              return (
                <div
                  key={experience.id}
                  className="flex-[0_0_80%] sm:flex-[0_0_50%] md:flex-[0_0_20%] pl-4"
                >
                  <div
                    className={cn(
                      "transition-transform duration-500 ease-in-out",
                      index === selectedIndex ? "scale-100" : "scale-90 opacity-85"
                    )}
                  >
                    <ExperienceCard
                      imageSrc={imageData.imageUrl}
                      imageAlt={imageData.description}
                      imageHint={imageData.imageHint}
                      isActive={index === selectedIndex}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Button
          onClick={scrollPrev}
          variant="ghost"
          size="icon"
          className="absolute top-1/2 -left-4 -translate-y-1/2 rounded-full h-16 w-16 bg-white/80 backdrop-blur-sm hover:bg-white shadow-md z-10"
        >
          <ArrowLeft className="h-10 w-10 text-primary" />
        </Button>
        <Button
          onClick={scrollNext}
          variant="ghost"
          size="icon"
          className="absolute top-1/2 -right-4 -translate-y-1/2 rounded-full h-16 w-16 bg-white/80 backdrop-blur-sm hover:bg-white shadow-md z-10"
        >
          <ArrowRight className="h-10 w-10 text-primary" />
        </Button>
      </div>

      <div className="text-center transition-all duration-500 ease-in-out min-h-[180px] flex items-center justify-center max-w-lg mx-auto mt-8">
        {selectedExperience && (
          <div className="animate-fade-in px-8">
            <h3 className="text-4xl font-bold">{selectedExperience.title}</h3>
            <p className="mt-4 text-lg opacity-90 max-w-2xl mx-auto">
              {selectedExperience.description}
            </p>
            <Button className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full text-lg px-10 py-6">
              Book Now
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
